import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import secureLocalStorage from 'react-secure-storage';
import { get_all_general_settings } from '../pages/organization-settings/api/organizationSettingApi';

interface GeneralSetting {
  setting_key: string;
  setting_value: any;
}

interface GeneralSettingsContextType {
  settings: GeneralSetting[];
  getSettingValue: (settingKey: string) => any;
  refreshSettings: () => Promise<void>;
  isLoading: boolean;
}

const GeneralSettingsContext = createContext<GeneralSettingsContextType | undefined>(undefined);

interface GeneralSettingsProviderProps {
  children: ReactNode;
}

export const GeneralSettingsProvider: React.FC<GeneralSettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<GeneralSetting[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize settings from localStorage
  useEffect(() => {
    const storedSettings = secureLocalStorage.getItem("general_settings");
    if (Array.isArray(storedSettings)) {
      setSettings(storedSettings);
    } else {
      // If not in localStorage, fetch from API
      loadSettings();
    }
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const response = await get_all_general_settings();
      if (response && response.data) {
        setSettings(response.data);
        secureLocalStorage.setItem("general_settings", response.data);
      }
    } catch (error) {
      console.error("Error loading general settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSettings = async () => {
    await loadSettings();
  };

  const getSettingValue = (settingKey: string): any => {
    if (Array.isArray(settings)) {
      const setting = settings.find(item => item.setting_key === settingKey);
      return setting ? setting.setting_value : null;
    }
    console.error('Settings data is not available or not in the correct format');
    return null;
  };

  const value = {
    settings,
    getSettingValue,
    refreshSettings,
    isLoading,
  };

  return (
    <GeneralSettingsContext.Provider value={value}>
      {children}
    </GeneralSettingsContext.Provider>
  );
};

export const useGeneralSettings = (): GeneralSettingsContextType => {
  const context = useContext(GeneralSettingsContext);
  if (context === undefined) {
    throw new Error('useGeneralSettings must be used within a GeneralSettingsProvider');
  }
  return context;
};

